import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Check, 
  ArrowLeft,
  Crown,
  Zap,
  Building2,
  Star,
  Gift,
  Loader2,
  Home
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import useUser from "@/hooks/useUser";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, isLoggedIn, refresh } = useUser();
  const [loading, setLoading] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);

  // Check if user already used trial
  const hasUsedTrial = user?.trial_used === true;
  const isOnTrial = user?.trial_active === true;
  const isPro = user?.plan === "pro" || user?.plan === "enterprise";

  const handleStartTrial = async () => {
    if (!isLoggedIn) {
      toast.error("Please login first to start your free trial");
      navigate("/login");
      return;
    }

    setTrialLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/start-trial`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success("🎉 7-day free trial activated! Enjoy Pro features!");
        refresh(); // Refresh user data
        navigate("/dashboard");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to start trial";
      toast.error(msg);
    } finally {
      setTrialLoading(false);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      period: "forever",
      icon: <Star className="w-6 h-6" />,
      color: "bg-gray-100",
      features: [
        "5 Keyword searches/day",
        "3 AI Content generations/day",
        "Basic SEO audit",
        "1 Scheduled post/day",
        "Color palette generator",
        "Community support"
      ],
      limitations: [
        "Limited API calls",
        "No logo generation",
        "No analytics"
      ]
    },
    {
      id: "starter",
      name: "Starter",
      price: 199,
      period: "/month",
      icon: <Zap className="w-6 h-6" />,
      color: "bg-gradient-to-r from-emerald-500 to-teal-500",
      badge: "For solo teachers & creators",
      features: [
        "50 Keyword searches/month",
        "20 AI Content generations/day",
        "5 AutoCaption videos/month (up to 3 min)",
        "10 PPT decks/month with premium themes",
        "5 Google Ads Copy generations/month",
        "All PDF tools (free tier)",
        "Email support"
      ],
      limitations: []
    },
    {
      id: "pro",
      name: "Pro",
      price: 499,
      period: "/month",
      icon: <Zap className="w-6 h-6" />,
      color: "bg-gradient-to-r from-blue-500 to-purple-500",
      popular: true,
      features: [
        "300 Keyword searches/month",
        "Unlimited Site Audits",
        "AI Content generations",
        "Unlimited scheduled posts",
        "Meta Ads Creative Downloader",
        "Priority support",
        "Export to CSV/PDF"
      ],
      limitations: []
    },
    {
      id: "enterprise",
      name: "Business",
      price: 1499,
      period: "/month",
      icon: <Building2 className="w-6 h-6" />,
      color: "bg-gradient-to-r from-yellow-500 to-orange-500",
      features: [
        "Everything in Pro",
        "Unlimited searches",
        "API access",
        "Team collaboration",
        "Custom integrations",
        "Dedicated account manager",
        "24/7 support"
      ],
      limitations: []
    }
  ];

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      navigate("/dashboard");
      return;
    }

    if (!isLoggedIn) {
      toast.error("Please login first to subscribe");
      navigate("/login");
      return;
    }

    setLoading(planId);
    
    try {
      // Use Stripe Checkout
      const packageId = planId === 'pro' ? 'pro_monthly' : 'enterprise_monthly';
      
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/stripe/checkout/create`,
        { 
          package_id: packageId,
          origin_url: window.location.origin,
          user_email: user?.email || ''
        },
        { withCredentials: true }
      );

      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(response.data.detail || "Failed to create checkout");
      }
      
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Unlock powerful marketing tools to grow your business. All plans include our core features.
          </p>
        </div>

        {/* Free Trial Banner - Only show for non-pro users who haven't used trial */}
        {isLoggedIn && !isPro && !hasUsedTrial && !isOnTrial && (
          <Card className="mb-8 p-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Try Pro Features Free for 7 Days!</h3>
                  <p className="text-violet-100">No credit card required. Cancel anytime.</p>
                </div>
              </div>
              <Button
                className="bg-white text-violet-700 hover:bg-gray-100 px-8"
                onClick={handleStartTrial}
                disabled={trialLoading}
                data-testid="start-trial-btn"
              >
                {trialLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Start Free Trial
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* On Trial Banner */}
        {isOnTrial && (
          <Card className="mb-8 p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600" />
              <p className="text-green-800">
                <strong>Trial Active!</strong> Your Pro trial ends on {new Date(user?.trial_ends).toLocaleDateString()}
              </p>
            </div>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`p-6 relative ${plan.popular ? 'border-2 border-violet-500 shadow-lg' : ''}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600">
                  Most Popular
                </Badge>
              )}
              {!plan.popular && plan.badge && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest">
                  {plan.badge}
                </Badge>
              )}

              <div className="text-center mb-6">
                <div className={`w-16 h-16 ${plan.color} rounded-2xl flex items-center justify-center mx-auto mb-4 text-white`}>
                  {plan.icon}
                </div>
                <h2 className="text-2xl font-bold">{plan.name}</h2>
                <div className="mt-4">
                  <span className="text-4xl font-bold">
                    {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-500">{plan.period}</span>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Free Trial Button for Pro Plan */}
              {plan.id === 'pro' && isLoggedIn && !isPro && !hasUsedTrial && (
                <Button
                  variant="outline"
                  className="w-full mb-3 border-violet-300 text-violet-600 hover:bg-violet-50"
                  onClick={handleStartTrial}
                  disabled={trialLoading}
                  data-testid="pro-card-trial-btn"
                >
                  {trialLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Try 7 Days Free
                    </>
                  )}
                </Button>
              )}

              <Button
                className={`w-full ${
                  plan.popular 
                    ? 'bg-violet-600 hover:bg-violet-700' 
                    : plan.id === 'enterprise'
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                    : ''
                }`}
                variant={plan.id === 'free' ? 'outline' : 'default'}
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading === plan.id}
                data-testid={`subscribe-${plan.id}`}
              >
                {loading === plan.id ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : plan.id === 'free' ? (
                  'Get Started'
                ) : (
                  <>
                    <Crown className="w-4 h-4 mr-2" />
                    Subscribe Now
                  </>
                )}
              </Button>
            </Card>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-8">
            <img 
              src="https://razorpay.com/assets/razorpay-glyph.svg" 
              alt="Razorpay" 
              className="h-8 opacity-50"
            />
            <div className="flex items-center gap-2 text-gray-400">
              <Check className="w-4 h-4" />
              <span className="text-sm">UPI</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Check className="w-4 h-4" />
              <span className="text-sm">Cards</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Check className="w-4 h-4" />
              <span className="text-sm">Net Banking</span>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
